<template>
  <DashboardContainer :pageTitle="pageTitle">
    <Spinner v-if="loading" class="mt-24" />
    <template v-else>
      <div class="campaign-data-shell">
        <section class="campaign-data-toolbar">
          <div class="campaign-data-dates">
            <div class="filter-field">
              <label for="campaign-data-start" class="filter-field-label">Start date</label>
              <FlatPickr
                id="campaign-data-start"
                name="start"
                class="input--text--small w-full placeholder:text-gray-400"
                placeholder="Select start date"
                :value="selectedDate.date1"
                @input="handleDateChange('date1', $event.target.value)"
              />
            </div>
            <div class="filter-field">
              <label for="campaign-data-end" class="filter-field-label">End date</label>
              <FlatPickr
                id="campaign-data-end"
                name="end"
                class="input--text--small w-full placeholder:text-gray-400"
                placeholder="Select end date"
                :value="selectedDate.date2"
                @input="handleDateChange('date2', $event.target.value)"
              />
            </div>
          </div>
          <div class="campaign-data-exports">
            <BaseButton
              variant="secondary"
              size="sm"
              :icon="ArrowDownTrayIcon"
              @click="exportCampaignData"
            >
              Export day
            </BaseButton>
            <BaseButton
              variant="secondary"
              size="sm"
              :icon="ArrowDownTrayIcon"
              @click="exportCampaignData"
            >
              Export range
            </BaseButton>
          </div>
        </section>

        <div class="campaign-data-stats">
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Assigned</p>
            <p class="campaign-data-stat-card__value">{{ tableData.length }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Available</p>
            <p class="campaign-data-stat-card__value">{{ fundraisers.pagination.totalCount }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Recurring</p>
            <p class="campaign-data-stat-card__value">{{ assignedRecurringTotal }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">One-time</p>
            <p class="campaign-data-stat-card__value">{{ assignedOneTimeTotal }}</p>
          </div>
        </div>

        <nav class="campaign-data-tabs" aria-label="Campaign fundraiser views">
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'assigned' }"
            @click="activeTab = 'assigned'"
          >
            <UserGroupIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Assigned
            <span class="campaign-data-tab-badge">{{ tableData.length }}</span>
          </button>
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'available' }"
            @click="activeTab = 'available'"
          >
            <UserPlusIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Available to assign
            <span class="campaign-data-tab-badge">{{ fundraisers.pagination.totalCount }}</span>
          </button>
        </nav>

        <section v-show="activeTab === 'assigned'" class="campaign-data-workspace">
          <Spinner v-if="updatingTable" class="mt-8" />
          <template v-else>
            <div class="space-y-3 md:hidden">
              <article v-for="i in tableData" :key="i.id" class="campaign-data-card">
                <div class="campaign-data-card__header">
                  <p class="truncate font-medium text-gray-900">{{ i.fundraiser_full_name }}</p>
                  <BaseButton
                    variant="danger"
                    size="xs"
                    @click="removeFundraiserFromCampaign(i.fundraiser)"
                  >
                    Remove
                  </BaseButton>
                </div>
                <dl class="campaign-data-card__stats">
                  <div class="campaign-data-stat-row">
                    <dt class="campaign-data-stat-label">Recurring</dt>
                    <dd class="campaign-data-stat-value">{{ i.recurring_donation_count }}</dd>
                  </div>
                  <div class="campaign-data-stat-row">
                    <dt class="campaign-data-stat-label">One-time</dt>
                    <dd class="campaign-data-stat-value">{{ i.one_time_donation_count }}</dd>
                  </div>
                  <div class="campaign-data-stat-row">
                    <dt class="campaign-data-stat-label">Hours</dt>
                    <dd>
                      <input
                        type="number"
                        :value="i.worked_hours"
                        step="0.01"
                        @change="handleWorkedHoursChange(i.id, $event)"
                        class="input--text--small w-24"
                        placeholder="Hours"
                      />
                    </dd>
                  </div>
                </dl>
              </article>
              <p
                v-if="!tableData.length"
                class="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500"
              >
                No fundraisers assigned to this campaign yet.
              </p>
            </div>

            <div class="hidden md:block">
              <BaseTable
                :columns="assignedColumns"
                :rows="tableData"
                row-key="id"
                :loading="updatingTable"
                empty-text="No fundraisers assigned to this campaign yet."
              >
                <template #cell-name="{ row }">
                  <span class="data-table__primary-text">{{ row.fundraiser_full_name }}</span>
                </template>
                <template #cell-recurring="{ row }">{{ row.recurring_donation_count }}</template>
                <template #cell-oneTime="{ row }">{{ row.one_time_donation_count }}</template>
                <template #cell-hours="{ row }">
                  <input
                    type="number"
                    :value="row.worked_hours"
                    step="0.01"
                    @change="handleWorkedHoursChange(row.id, $event)"
                    class="input--text--small w-24"
                    placeholder="Hours"
                  />
                </template>
                <template #cell-actions="{ row }">
                  <BaseButton
                    variant="danger"
                    size="xs"
                    @click="removeFundraiserFromCampaign(row.fundraiser)"
                  >
                    Remove
                  </BaseButton>
                </template>
              </BaseTable>
            </div>
          </template>
        </section>

        <section v-show="activeTab === 'available'" class="campaign-data-workspace">
          <div class="campaign-data-filter-bar">
            <FilterMultiselect
              field-label="Fundraisers"
              v-model="selectedFundraiserFilter"
              :options="fundraiserFiltersIndexes"
              :multiple="true"
              :close-on-select="false"
              placeholder="Select fundraisers"
              selectLabel="Apply"
              deselectLabel="Remove"
              label="fullName"
              track-by="id"
              clearable
            />
            <FilterMultiselect
              field-label="Home offices"
              v-model="selectedHomeOfficeFilter"
              :options="homeOfficesFiltersIndexes"
              :multiple="true"
              :close-on-select="false"
              placeholder="Select home offices"
              selectLabel="Apply"
              deselectLabel="Remove"
              label="name"
              track-by="id"
              clearable
            />
            <div class="campaign-data-filter-actions">
              <BaseButton
                v-if="hasFilterSelection"
                variant="secondary"
                size="sm"
                @click="openSaveFilterModal"
              >
                Save filter
              </BaseButton>
              <BaseButton variant="secondary" size="sm" @click="showLoadFiltersModal = true">
                Load saved filters
              </BaseButton>
              <BaseButton
                variant="ghost"
                size="sm"
                :disabled="!hasFilterSelection"
                @click="clearSelectedFilters"
              >
                Clear filters
              </BaseButton>
            </div>
          </div>

          <Spinner v-if="updatingTable" class="mt-8" />
          <template v-else>
            <div class="space-y-3 md:hidden">
              <article
                v-for="fundraiser in availableFundraisers"
                :key="fundraiser.id"
                class="campaign-data-card"
              >
                <div class="campaign-data-card__header">
                  <div class="min-w-0">
                    <p class="truncate font-medium text-gray-900">{{ fundraiser.fullName }}</p>
                    <p class="truncate text-xs text-gray-500">{{ fundraiser.email }}</p>
                  </div>
                  <BaseButton
                    v-if="isCurrentDate"
                    variant="primary"
                    size="xs"
                    :disabled="isFundraiserAssigned(fundraiser)"
                    @click="assignFundraiserToCampaign(fundraiser)"
                  >
                    Assign
                  </BaseButton>
                </div>
                <p class="mt-2 text-sm text-gray-600">
                  {{ fundraiser.homeOffice ? fundraiser.homeOffice.name : 'N/A' }}
                </p>
              </article>
              <p
                v-if="!availableFundraisers.length"
                class="rounded-xl bg-gray-50 px-4 py-10 text-center text-sm text-gray-500"
              >
                No available fundraisers match your filters.
              </p>
            </div>

            <div class="hidden md:block">
              <BaseTable
                :columns="availableColumns"
                :rows="availableFundraisers"
                row-key="id"
                :loading="updatingTable"
                empty-text="No available fundraisers match your filters."
              >
                <template #cell-name="{ row }">
                  <span class="data-table__primary-text">{{ row.fullName }}</span>
                </template>
                <template #cell-email="{ row }">{{ row.email }}</template>
                <template #cell-office="{ row }">
                  {{ row.homeOffice ? row.homeOffice.name : 'N/A' }}
                </template>
                <template #cell-actions="{ row }">
                  <BaseButton
                    v-if="isCurrentDate"
                    variant="primary"
                    size="xs"
                    :disabled="isFundraiserAssigned(row)"
                    @click="assignFundraiserToCampaign(row)"
                  >
                    Assign
                  </BaseButton>
                </template>
              </BaseTable>
            </div>
          </template>

          <BasePagination
            v-if="fundraisers.list?.length"
            v-model="itemsPerPage"
            :page="fundraisers.pagination.page"
            :total-pages="totalPages"
            :total-count="fundraisers.pagination.totalCount"
            :display-start="displayStart"
            :display-end="displayEnd"
            :has-prev-page="fundraisers.pagination.hasPrevPage"
            :has-next-page="fundraisers.pagination.hasNextPage"
            @page-size-change="updateItemsPerPage"
            @prev="prevPage"
            @next="nextPage"
          />
        </section>
      </div>

      <div class="mt-6 flex justify-end">
        <BaseButton variant="secondary" @click="$router.push({ name: 'CampaignList' })">
          Back to campaigns
        </BaseButton>
      </div>

      <div
        v-if="showSaveFilterModal"
        class="fixed inset-0 z-50 overflow-hidden"
        aria-labelledby="campaign-data-save-filter-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="absolute inset-0 bg-gray-500/75 transition-opacity"
            @click="showSaveFilterModal = false"
            aria-hidden="true"
          />
          <div class="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div class="w-screen max-w-md">
              <div class="campaign-data-modal-panel">
                <div class="campaign-data-modal-body">
                  <div class="flex items-start justify-between gap-3">
                    <h2
                      class="text-lg font-semibold text-gray-900"
                      id="campaign-data-save-filter-title"
                    >
                      Save filter
                    </h2>
                    <button
                      type="button"
                      class="-m-2 rounded-lg p-2 text-gray-400 transition-colors hover:text-gray-600"
                      @click="showSaveFilterModal = false"
                    >
                      <span class="sr-only">Close panel</span>
                      <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                  <InputField
                    v-model:value="filterTemplateName"
                    label="Filter name"
                    label-size="small"
                    input-text-size="small"
                    placeholder="Enter a name for this filter"
                  />
                </div>
                <div class="campaign-data-modal-footer">
                  <BaseButton variant="ghost" @click="showSaveFilterModal = false"
                    >Cancel</BaseButton
                  >
                  <BaseButton
                    variant="primary"
                    :disabled="!filterTemplateName.trim()"
                    @click="saveCampaignDataFilter"
                  >
                    Save
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showLoadFiltersModal"
        class="fixed inset-0 z-50 overflow-hidden"
        aria-labelledby="campaign-data-load-filters-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="absolute inset-0 bg-gray-500/75 transition-opacity"
            @click="showLoadFiltersModal = false"
            aria-hidden="true"
          />
          <div class="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div class="w-screen max-w-md">
              <div class="campaign-data-modal-panel">
                <div class="campaign-data-modal-body">
                  <div class="flex items-start justify-between gap-3">
                    <h2
                      class="text-lg font-semibold text-gray-900"
                      id="campaign-data-load-filters-title"
                    >
                      Saved filters
                    </h2>
                    <button
                      type="button"
                      class="-m-2 rounded-lg p-2 text-gray-400 transition-colors hover:text-gray-600"
                      @click="showLoadFiltersModal = false"
                    >
                      <span class="sr-only">Close panel</span>
                      <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                  <div v-if="loadingSavedFilters" class="py-8 text-center">
                    <Spinner />
                  </div>
                  <div v-else class="space-y-3">
                    <article
                      v-for="item in savedCampaignDataFilters"
                      :key="item.id"
                      class="campaign-data-saved-filter"
                    >
                      <span class="min-w-0 truncate text-sm font-medium text-gray-900">{{
                        item.name
                      }}</span>
                      <div class="flex shrink-0 gap-2">
                        <BaseButton variant="primary" size="xs" @click="applySavedFilter(item)">
                          Apply
                        </BaseButton>
                        <BaseButton variant="danger" size="xs" @click="removeSavedFilter(item.id)">
                          Remove
                        </BaseButton>
                      </div>
                    </article>
                    <p
                      v-if="!savedCampaignDataFilters.length"
                      class="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-500"
                    >
                      No saved filters for this campaign
                    </p>
                  </div>
                </div>
                <div class="campaign-data-modal-footer">
                  <BaseButton variant="secondary" block @click="showLoadFiltersModal = false">
                    Close
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { onBeforeMount, ref, triggerRef, reactive, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import DashboardContainer from '../components/DashboardContainer.vue'
import Spinner from '@/components/Spinner'
import { CampaignApi } from '@/services/campaign/'
import { DonationApi } from '@/services/donations/'
import { fundraiserFunctions, UserApi } from '@/services/users/'
import { WorkedHoursApi } from '@/services/workingHours'
import { CAMPAIGN_DATA_FUNDRAISER_FILTER_TYPE } from '@/services/workingHours/models'
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import InputField from '@/components/inputs/InputField'
import { nextTick } from 'vue'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { getTodayInEastern } from '@/utils/dateHelpers'

export default {
  name: 'CampaignData',
  components: {
    DashboardContainer,
    Spinner,
    FlatPickr,
    XMarkIcon,
    InputField,
    UserGroupIcon,
    UserPlusIcon,
  },
  setup() {
    const route = useRoute()
    const campaignId = route.params.id
    const toast = useToast()
    const campaign = ref()
    const { fundraiserCollection, fundraiserFilters } = fundraiserFunctions()
    const fundraisers = ref(fundraiserCollection)
    const assignedFundraisers = ref([])
    const availableFundraisers = ref([])
    const hours = ref()
    const tableData = ref([])
    const loading = ref(false)
    const updatingTable = ref(false)
    const workedHoursMap = reactive({})
    const pageTitle = ref('')
    const totalPages = ref()
    const itemsPerPage = ref(50)
    const filterIndexes = reactive({
      fundraisersFilter: [],
      homeOfficesFilter: [],
    })
    const fundraiserFiltersIndexes = computed(() => filterIndexes.fundraisersFilter)
    const homeOfficesFiltersIndexes = computed(() => {
      return [...filterIndexes.homeOfficesFilter].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase()
        const nameB = (b.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    })
    const selectedFundraiserFilter = ref([])
    const selectedHomeOfficeFilter = ref([])

    const hasFilterSelection = computed(() => {
      return selectedFundraiserFilter.value.length > 0 || selectedHomeOfficeFilter.value.length > 0
    })

    const showSaveFilterModal = ref(false)
    const showLoadFiltersModal = ref(false)
    const filterTemplateName = ref('')
    const savedCampaignDataFilters = ref([])
    const loadingSavedFilters = ref(false)
    const activeTab = ref('available')

    const syncDefaultTab = () => {
      const rows = Array.isArray(tableData.value) ? tableData.value : []
      activeTab.value = rows.length > 0 ? 'assigned' : 'available'
    }

    const assignedColumns = [
      { key: 'name', label: 'Fundraiser', minWidth: '12rem' },
      { key: 'recurring', label: 'Recurring', align: 'center' },
      { key: 'oneTime', label: 'One-time', align: 'center' },
      { key: 'hours', label: 'Worked hours', minWidth: '8rem' },
      { key: 'actions', label: '', align: 'right' },
    ]

    const availableColumns = [
      { key: 'name', label: 'Fundraiser', minWidth: '12rem' },
      { key: 'email', label: 'Email', minWidth: '14rem' },
      { key: 'office', label: 'Home office', minWidth: '10rem' },
      { key: 'actions', label: '', align: 'right' },
    ]

    const assignedRecurringTotal = computed(() =>
      tableData.value.reduce((sum, row) => sum + (row.recurring_donation_count || 0), 0),
    )

    const assignedOneTimeTotal = computed(() =>
      tableData.value.reduce((sum, row) => sum + (row.one_time_donation_count || 0), 0),
    )

    const getSavedTemplateFilterType = (t) => {
      const raw = t?.type ?? t?.filterType ?? t?.template?.filter_type ?? t?.template?.filterType
      return typeof raw === 'string' ? raw.trim() : raw
    }

    const openSaveFilterModal = () => {
      filterTemplateName.value = ''
      showSaveFilterModal.value = true
    }

    const clearSelectedFilters = () => {
      selectedFundraiserFilter.value = []
      selectedHomeOfficeFilter.value = []
    }

    const fetchSavedCampaignDataFilters = async () => {
      try {
        loadingSavedFilters.value = true
        const response = await DonationApi.csc.getExportTemplate()
        const all = Array.isArray(response) ? response : response?.results ?? []
        savedCampaignDataFilters.value = all.filter((t) => {
          if (getSavedTemplateFilterType(t) !== CAMPAIGN_DATA_FUNDRAISER_FILTER_TYPE) {
            return false
          }
          const d = t.template || {}
          const cid = d.campaign
          return cid && String(cid) === String(campaignId)
        })
      } catch (error) {
        toast.error('Failed to load saved filters')
        console.error(error)
      } finally {
        loadingSavedFilters.value = false
      }
    }

    const saveCampaignDataFilter = async () => {
      try {
        setFilters()
        await CampaignApi.csc.saveCampaignDataFilterTemplate({
          name: filterTemplateName.value.trim(),
          filterType: CAMPAIGN_DATA_FUNDRAISER_FILTER_TYPE,
          campaign: campaignId,
          fundraiser: fundraiserFilters.fundraiser || '',
          homeOffice: fundraiserFilters.homeOffice || '',
        })
        filterTemplateName.value = ''
        showSaveFilterModal.value = false
        toast.success('Filter saved successfully')
      } catch (error) {
        alert(error)
      }
    }

    const applySavedFilter = (template) => {
      const d = template.template || {}
      const fundStr = d.fundraiser || ''
      const hoStr = d.home_office || d.homeOffice || ''

      if (fundStr) {
        const ids = fundStr
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        selectedFundraiserFilter.value = filterIndexes.fundraisersFilter.filter((f) =>
          ids.includes(String(f.id)),
        )
      } else {
        selectedFundraiserFilter.value = []
      }

      if (hoStr) {
        const ids = hoStr
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        selectedHomeOfficeFilter.value = filterIndexes.homeOfficesFilter.filter((h) =>
          ids.includes(String(h.id)),
        )
      } else {
        selectedHomeOfficeFilter.value = []
      }

      showLoadFiltersModal.value = false
    }

    const removeSavedFilter = async (templateId) => {
      try {
        await DonationApi.csc.removeSavedFilterTemplate({ templateId })
        toast.success('Filter removed')
        await fetchSavedCampaignDataFilters()
      } catch (error) {
        toast.error('Failed to remove filter')
        console.error(error)
      }
    }

    const isCurrentDate = computed(() => {
      return selectedDate.date1 === today
    })

    onBeforeMount(async () => {
      loading.value = true
      try {
        campaign.value = await CampaignApi.csc.customRetrieve({
          campaign: campaignId,
        })
        pageTitle.value = campaign.value.location
          ? campaign.value.name + ' ' + campaign.value.location + ' Campaign Data'
          : campaign.value.name + ' ' + 'Campaign Data'
        const campaignCanvassingOrgId =
          typeof campaign.value.canvassingOrg === 'string'
            ? campaign.value.canvassingOrg
            : campaign.value.canvassingOrg?.id || ''
        fundraiserFilters.canvassingOrg = campaignCanvassingOrgId
        fundraiserFilters.userStatus = 'active'
        fundraisers.value.pagination.size = itemsPerPage.value
        await fundraisers.value.refresh().catch((error) => {
          alert(error)
        })
        console.log('Donation')
        totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
        assignedFundraisers.value = campaign.value.fundraisers
        availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)
        hours.value = await fetchAndLogWorkedHours()
        updateWorkedHoursMap()
        console.log(hours.value.results)
        tableData.value = hours.value?.results ?? []
        syncDefaultTab()

        const response = await UserApi.csc.retrieveFilterIndexes({
          canvassingOrg: campaignCanvassingOrgId || undefined,
        })
        filterIndexes.fundraisersFilter = response.fundraisers || []
        filterIndexes.homeOfficesFilter = response.homeOffices || []
      } catch (e) {
        console.log(e)
      }
      triggerRef(fundraisers)
      triggerRef(hours)
      loading.value = false
    })

    const getFundraisers = async () => {
      updatingTable.value = true
      setFilters()
      fundraisers.value.pagination.page = 1
      fundraisers.value.pagination.size = itemsPerPage.value
      await fundraisers.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(fundraisers)
      availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)
      totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
    }

    const isFundraiserAssigned = (fundraiser) => {
      return assignedFundraisers.value.some((assigned) => assigned.id === fundraiser.id)
    }

    const displayStart = computed(() => {
      return (fundraisers.value.pagination.page - 1) * itemsPerPage.value + 1
    })

    const displayEnd = computed(() => {
      const end = fundraisers.value.pagination.page * itemsPerPage.value
      return end > fundraisers.value.pagination.totalCount
        ? fundraisers.value.pagination.totalCount
        : end
    })

    const setFilters = () => {
      fundraiserFilters.fundraiser = selectedFundraiserFilter.value
        .map((fundraiser) => fundraiser.id)
        .join(',')
      fundraiserFilters.homeOffice = selectedHomeOfficeFilter.value
        .map((homeOffice) => homeOffice.id)
        .join(',')
    }

    const fetchAndLogWorkedHours = async () => {
      try {
        const response = await WorkedHoursApi.csc.retrieve({
          campaign: campaignId,
          date: selectedDate.date1,
        })
        return response
      } catch (error) {
        console.error('Error fetching worked hours:', error)
      }
    }

    // Filter fundraisers based on if they are not currently assigned to the current campaign
    // BB note: This will be regardless of if they're assigned to other campaigns so
    // this may need to be changed to only grab fundraisers that are assigned to no campaigns
    const filterAvailableFundraisers = (fundraisers) => {
      return fundraisers.filter((fundraiser) => fundraiser.isActive)
    }

    const removeFundraiserFromCampaign = async (fundraiser) => {
      loading.value = true

      const today = getTodayInEastern()
      const selectedStartDate = selectedDate.date1
      const currentHourEntry = tableData.value.find(
        (row) => row.fundraiser === fundraiser || row.fundraiser?.id === fundraiser,
      )

      try {
        if (
          selectedStartDate === today &&
          currentHourEntry &&
          currentHourEntry.recurring_donation_count == 0 &&
          currentHourEntry.one_time_donation_count == 0
        ) {
          // Current date: keep original behavior (fully remove from campaign)
          let updatedAssignedFundraisers = assignedFundraisers.value.filter(
            (fund) => fund.id !== fundraiser,
          )
          let assignedFundraiserIds = updatedAssignedFundraisers.map((fund) => fund.id)
          let updatedCampaign = {
            id: campaignId,
            fundraisers: assignedFundraiserIds,
          }

          campaign.value = await CampaignApi.csc.customUpdate(updatedCampaign)
          await fundraisers.value.refresh().catch((error) => {
            alert(error)
          })

          // Update local state
          assignedFundraisers.value = updatedAssignedFundraisers
          availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)

          // Fetch updated worked hours and update tableData
          hours.value = await fetchAndLogWorkedHours()
          tableData.value = hours.value?.results ?? []
        } else {
          // Previous date: only clear the worked hours for this specific date
          if (
            currentHourEntry &&
            currentHourEntry.recurring_donation_count == 0 &&
            currentHourEntry.one_time_donation_count == 0
          ) {
            await WorkedHoursApi.csc.deleteWorkingHour(currentHourEntry)
          }

          // Refresh worked hours for the selected date
          hours.value = await fetchAndLogWorkedHours()
          tableData.value = hours.value?.results ?? []
        }

        loading.value = false
      } catch (e) {
        console.log(e)
        loading.value = false
      }
    }

    const assignFundraiserToCampaign = async (fundraiser) => {
      const scrollPosition = window.scrollY
      loading.value = true
      let updatedAssignedFundraisers = [...assignedFundraisers.value, fundraiser]
      let assignedFundraiserIds = updatedAssignedFundraisers.map((fund) => fund.id)
      let updatedCampaign = {
        id: campaignId,
        fundraisers: assignedFundraiserIds,
      }
      try {
        campaign.value = await CampaignApi.csc.customUpdate(updatedCampaign)
        await fundraisers.value.refresh().catch((error) => {
          alert(error)
        })

        // Update local state
        assignedFundraisers.value = updatedAssignedFundraisers
        availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)

        // Fetch updated worked hours and update tableData
        hours.value = await fetchAndLogWorkedHours()
        tableData.value = hours.value?.results ?? []
        loading.value = false
        nextTick(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'auto' })
        })
      } catch (e) {
        console.log(e)
      }
    }

    const updateWorkedHoursMap = () => {
      assignedFundraisers.value.forEach((fundraiser) => {
        workedHoursMap[fundraiser.id] = getWorkedHours(fundraiser)
      })
    }

    const setLocalWorkedHours = (workedHourId, savedHours) => {
      const lists = [tableData.value, hours.value?.results]
      lists.forEach((list) => {
        if (!Array.isArray(list)) return
        const item = list.find((obj) => obj.id === workedHourId)
        if (item) {
          item.worked_hours = savedHours
        }
      })
    }

    const updateWorkedHours = async (workedHourId, newWorkedHours) => {
      try {
        const oldWorkedHours = getOutdatedWorkedHour(workedHourId)

        const updatedWorkedHours = oldWorkedHours + newWorkedHours

        const updatePayload = {
          id: workedHourId,
          worked_hours: updatedWorkedHours,
        }

        await WorkedHoursApi.csc.customUpdate(updatePayload)
        setLocalWorkedHours(workedHourId, updatedWorkedHours)
        updateWorkedHoursMap()
        return true
      } catch (e) {
        console.log(e)
        return false
      }
    }

    const getOutdatedWorkedHour = (id) => {
      const list = hours.value.results
      const outdatedWorkedHourObj = list.find((obj) => obj.id === id)
      return outdatedWorkedHourObj ? parseFloat(outdatedWorkedHourObj.workedHours || 0) : 0
    }

    const MIN_HOURS_WITH_DONATIONS = 0.1

    const hasDonationsOnRow = (row) => {
      return (row?.recurring_donation_count || 0) > 0 || (row?.one_time_donation_count || 0) > 0
    }

    const handleWorkedHoursChange = async (workedHourId, event) => {
      const parsedHours = parseFloat(event.target.value)
      const newWorkedHours = Number.isNaN(parsedHours) ? 0 : parsedHours
      const row = tableData.value.find((item) => item.id === workedHourId)
      const previousHours = row?.worked_hours ?? 0

      if (hasDonationsOnRow(row) && newWorkedHours < MIN_HOURS_WITH_DONATIONS) {
        toast.error(
          'Worked hours cannot be below 0.1 when this fundraiser has donations on this campaign for this day.',
        )
        event.target.value = previousHours
        return
      }

      workedHoursMap[workedHourId] = newWorkedHours
      const saved = await updateWorkedHours(workedHourId, newWorkedHours)
      if (!saved) {
        event.target.value = previousHours
      }
    }

    const getDailyWorkedHours = (fundraiser) => {
      const filteredWorkedHours = hours.value.results
      return filteredWorkedHours.filter(
        (hour) => hour.fundraiser === fundraiser.id && hour.campaign === campaignId,
      )
    }

    const getWorkedHours = (fundraiser) => {
      const list = getDailyWorkedHours(fundraiser)

      return list.reduce((total, hour) => total + parseFloat(hour.worked_hours || 0), 0)
    }
    const today = getTodayInEastern()
    const selectedDate = reactive({
      date1: today,
      date2: '',
    })

    const getDateRangeOption1 = (date) => {
      if (date) {
        // date = date.replace(/-/g, '/')
        selectedDate.date1 = date
      } else {
        selectedDate.date1 = ''
      }
    }

    const getDateRangeOption2 = (date) => {
      if (date) {
        // date = date.replace(/-/g, '/')
        selectedDate.date2 = date
      } else {
        selectedDate.date2 = ''
      }
    }
    const exportCampaignData = async () => {
      if (!selectedDate.date2) {
        let startDate = new Date(selectedDate.date1)
        const formattedStartDate = startDate.toISOString().split('T')[0]
        try {
          await WorkedHoursApi.csc.customRetrieve({
            campaign: campaignId,
            date: formattedStartDate,
            export_file: 'true',
          })
        } catch (error) {
          console.error('Error exporting range data:', error)
        }
      } else {
        let startDate = new Date(selectedDate.date1)
        let endDate = new Date(selectedDate.date2)
        if (startDate > endDate) {
          console.warn('Start date is later than end date. Swapping dates.')[(startDate, endDate)] =
            [endDate, startDate]
        }
        const formattedStartDate = startDate.toISOString().split('T')[0]
        const formattedEndDate = endDate.toISOString().split('T')[0]

        try {
          await WorkedHoursApi.csc.rangeRetrieve({
            campaign: campaignId,
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            export_file: 'true',
          })
        } catch (error) {
          console.error('Error exporting range data:', error)
        }
      }
    }

    const nextPage = async () => {
      if (fundraisers.value.pagination.hasNextPage) {
        updatingTable.value = true
        fundraisers.value.pagination.size = itemsPerPage.value
        await fundraisers.value.nextPage().catch((error) => {
          alert(error)
        })
        triggerRef(fundraisers)
        availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)
        totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }

    const prevPage = async () => {
      if (fundraisers.value.pagination.hasPrevPage) {
        updatingTable.value = true
        fundraisers.value.pagination.size = itemsPerPage.value
        await fundraisers.value.prevPage().catch((error) => {
          alert(error)
        })
        triggerRef(fundraisers)
        availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)
        totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
        updatingTable.value = false
      }
    }
    const updateItemsPerPage = async () => {
      updatingTable.value = true
      fundraisers.value.pagination.size = itemsPerPage.value
      fundraisers.value.pagination.page = 1
      await fundraisers.value.refresh().catch((error) => {
        alert(error)
      })
      triggerRef(fundraisers)
      availableFundraisers.value = filterAvailableFundraisers(fundraisers.value.list)
      totalPages.value = Math.ceil(fundraisers.value.pagination.totalCount / itemsPerPage.value)
      updatingTable.value = false
    }
    const handleDateChange = (dateField, value) => {
      selectedDate[dateField] = value
    }

    watch(showLoadFiltersModal, (open) => {
      if (open) {
        fetchSavedCampaignDataFilters()
      }
    })

    watch(
      [
        () => selectedDate.date1,
        () => selectedFundraiserFilter.value,
        () => selectedHomeOfficeFilter.value,
      ],
      async (newDate, oldDate) => {
        // Check if either selectedFundraiserFilter.value or selectedHomeOfficeFilter.value changed
        const fundraiserChanged = selectedFundraiserFilter.value !== oldDate[1]
        const homeOfficeChanged = selectedHomeOfficeFilter.value !== oldDate[2]

        if (fundraiserChanged || homeOfficeChanged) {
          await getFundraisers()
        } else {
          updatingTable.value = true
          try {
            updateWorkedHoursMap()
            hours.value = await fetchAndLogWorkedHours()
            tableData.value = hours.value?.results ?? []
          } finally {
            updatingTable.value = false
          }
        }
      },
    )

    return {
      selectedFundraiserFilter,
      selectedHomeOfficeFilter,
      fundraiserFiltersIndexes,
      homeOfficesFiltersIndexes,
      isFundraiserAssigned,
      displayStart,
      displayEnd,
      itemsPerPage,
      updateItemsPerPage,
      fundraisers,
      prevPage,
      nextPage,
      totalPages,
      campaign,
      assignedFundraisers,
      availableFundraisers,
      loading,
      updatingTable,
      removeFundraiserFromCampaign,
      assignFundraiserToCampaign,
      getDateRangeOption1,
      getDateRangeOption2,
      selectedDate,
      getWorkedHours,
      workedHoursMap,
      updateWorkedHours,
      handleWorkedHoursChange,
      fetchAndLogWorkedHours,
      tableData,
      exportCampaignData,
      pageTitle,
      handleDateChange,
      isCurrentDate,
      hasFilterSelection,
      showSaveFilterModal,
      showLoadFiltersModal,
      filterTemplateName,
      savedCampaignDataFilters,
      loadingSavedFilters,
      openSaveFilterModal,
      clearSelectedFilters,
      saveCampaignDataFilter,
      applySavedFilter,
      removeSavedFilter,
      ArrowDownTrayIcon,
      activeTab,
      assignedColumns,
      availableColumns,
      assignedRecurringTotal,
      assignedOneTimeTotal,
    }
  },
}
</script>
